import styled from '@emotion/styled';

import InfoIcon from '@/assets/svg/ui-elements/info-circle-alt.svg';
import UserIcon from '@/assets/svg/ui-elements/user-outline.svg';
import { Avatar, hoverable, Tooltip, Typography } from '@/design-system';
import { theme } from '@/design-system/theme';
import { truncateAddress } from '@/shared/modules/wallet/send/recipient';
import { CryptoAsset } from '@/shared/modules/wallet/use-crypto-assets';

import { CoinIcon } from '../../crypto-assets/coin-icon';
import { fmtUsdShort } from '../../crypto-assets/formatters';
import { getRecipientLabel, getRecipientName } from './recipient-name';
import type { ConfirmationRecipient } from './use-send-flow';

const Card = styled('div')(({ theme: t }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: t.spacing[12],
  boxSizing: 'border-box',
  minHeight: 72,
  padding: '12px 16px',
  borderRadius: t.radius[16],
  backgroundColor: t.colors.background.overlay,
  border: `1px solid ${t.colors.border.default}`,
}));

const CardText = styled('div')(({ theme: t }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: t.spacing[8],
  flex: 1,
  minWidth: 0,
}));

/** Yields its width first, so a long name ellipsizes rather than pushing the address out. */
const RecipientName = styled(Typography)({ minWidth: 0 });

const InlineAddress = styled(Typography)({ flexShrink: 0 });

const RecipientAddress = styled(Typography)({ wordBreak: 'break-all' });

const AvatarImage = styled('img')(({ theme: t }) => ({
  display: 'block',
  width: '100%',
  height: '100%',
  borderRadius: t.radius.full,
  objectFit: 'cover',
}));

const UserBadge = styled('span')(({ theme: t }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 32,
  height: 32,
  color: t.colors.icon.primary,

  '& svg': { width: '100%', height: '100%' },
}));

const Rows = styled('dl')(({ theme: t }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: t.spacing[8],
  margin: 0,
  paddingTop: t.spacing[16],
  borderTop: `1px solid ${t.colors.border.default}`,
  overflow: 'clip',
}));

const Row = styled('div')(({ theme: t }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: t.spacing[16],
}));

const Term = styled('dt')({ flexShrink: 0 });

const Value = styled('dd')({
  margin: 0,
  minWidth: 0,
  textAlign: 'right',
  wordBreak: 'break-all',
});

const FeeValue = styled('div')(({ theme: t }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: t.spacing[4],
  color: t.colors.text.secondary,

  '& svg': { width: 14, height: 14, flexShrink: 0 },
}));

const FeeInfoButton = styled('button')(({ theme: t }) => ({
  ...hoverable(t, 'lift'),
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 14,
  height: 14,
  padding: 0,
  border: 0,
  color: t.colors.text.secondary,
  background: 'transparent',
  cursor: 'help',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: -15,
  },

  '&:focus-visible': {
    outline: `2px solid ${t.colors.border.focus}`,
    outlineOffset: 2,
  },
}));

export const SendAmountCard = ({
  coin,
  amount,
  amountUsd,
}: {
  coin: CryptoAsset;
  amount: string;
  amountUsd: number | null;
}) => (
  <Card>
    <CoinIcon symbol={coin.symbol} iconUrl={coin.iconUrl} size={32} />
    <CardText>
      <Typography variant="h6" color={theme.colors.text.primary}>
        {amount}
      </Typography>
      {amountUsd != null && (
        <Typography variant="body3" color={theme.colors.text.secondary}>
          {fmtUsdShort(amountUsd)}
        </Typography>
      )}
    </CardText>
    <Typography variant="body2" color={theme.colors.text.secondary}>
      {coin.symbol}
    </Typography>
  </Card>
);

export const SendRecipientCard = ({ recipient }: { recipient: ConfirmationRecipient }) => {
  const { address, identity } = recipient;

  if (!identity) {
    return (
      <Card>
        <UserBadge aria-hidden>
          <UserIcon />
        </UserBadge>
        <RecipientAddress variant="h6" color={theme.colors.text.primary}>
          {truncateAddress(address)}
        </RecipientAddress>
      </Card>
    );
  }

  const name = getRecipientName(identity);

  return (
    <Card>
      <Avatar
        size="sm"
        color="green"
        variant="subtle"
        initials={name}
        type={identity.iconUrl ? 'icon' : 'initials'}
        icon={identity.iconUrl ? <AvatarImage src={identity.iconUrl} alt={name} /> : undefined}
      />
      <CardText>
        <RecipientName variant="h6" color={theme.colors.text.primary} noWrap>
          {getRecipientLabel(identity)}
        </RecipientName>
        <InlineAddress variant="body3" color={theme.colors.text.secondary} noWrap>
          {truncateAddress(address)}
        </InlineAddress>
      </CardText>
    </Card>
  );
};

interface SendSummaryProps {
  amount: string;
  symbol: string;
  recipientAddress: string;
  network: string;
  networkFee: string;
  feeTooltip: string;
}

export const SendSummary = ({
  amount,
  symbol,
  recipientAddress,
  network,
  networkFee,
  feeTooltip,
}: SendSummaryProps) => (
  <Rows>
    <Row>
      <Term>
        <Typography variant="body2" color={theme.colors.text.secondary}>
          You are sending:
        </Typography>
      </Term>
      <Value>
        <Typography variant="body2" fontWeight={600} color={theme.colors.text.primary}>
          {amount} {symbol}
        </Typography>
      </Value>
    </Row>
    <Row>
      <Term>
        <Typography variant="body2" color={theme.colors.text.secondary}>
          Recipient address:
        </Typography>
      </Term>
      <Value>
        <Typography variant="body2" fontWeight={600} color={theme.colors.text.primary}>
          {truncateAddress(recipientAddress, 8, 8)}
        </Typography>
      </Value>
    </Row>
    <Row>
      <Term>
        <Typography variant="body2" color={theme.colors.text.secondary}>
          Network:
        </Typography>
      </Term>
      <Value>
        <Typography variant="body2" fontWeight={600} color={theme.colors.text.primary}>
          {network}
        </Typography>
      </Value>
    </Row>
    <Row>
      <Term>
        <Typography variant="body2" color={theme.colors.text.secondary}>
          Network fees:
        </Typography>
      </Term>
      <Value>
        <FeeValue>
          <Typography as="span" variant="body2" fontWeight={600} color={theme.colors.text.primary}>
            {networkFee}
          </Typography>
          <Tooltip title={feeTooltip} side="left">
            <FeeInfoButton type="button" aria-label="Network fee information">
              <InfoIcon />
            </FeeInfoButton>
          </Tooltip>
        </FeeValue>
      </Value>
    </Row>
  </Rows>
);
